import { pluralise_word } from "base/functions";
import URLHelper from "base/URLHelper";
import ValueHelperConfiguration from "./ValueHelperConfiguration";

class ValueHelper {

    static clamp(min, value, max) {
        return Math.max(min, Math.min(value, max));
    }

    static ensure_formatted(html, allowed_elements = "") {
        html = html.trim();

        if (!/^<p>/.test(html) || allowed_elements !== "") {
            return "<p>" + ValueHelper.stringify_html(html, false, allowed_elements).replace(/\n+/g, "</p><p>") + "</p>";
        }

        return html;
    }

    static format_bytes(bytes, decimals = 0, separator = " ") {
        if (bytes === 0) return "0 Bytes";
        let k = 1000;
        let sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        let i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + separator + sizes[i];
    }

    static format_money(value, sign = null, round = null) {
        sign = sign === null ? ValueHelperConfiguration.default_money_sign : sign;
        round = round === null ? ValueHelperConfiguration.default_money_round : round;

        //noinspection EqualityComparisonWithCoercionJS
        return (value < 0 ? "-" : "") + sign + ValueHelper.format_thousands(Math.abs(parseFloat(value)).toFixed(round && parseInt(value) == parseFloat(value) ? 0 : 2));
    }

    static format_thousands(value) {
        let parts = value.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join(".");
    }

    static format_time(seconds) {
        let hours = Math.floor(seconds / 3600);
        let minutes = Math.floor((seconds - (hours * 3600)) / 60);
        seconds = seconds - (hours * 3600) - (minutes * 60);

        if (seconds < 10) seconds = "0" + seconds;
        if (hours > 0) {
            if (minutes < 10) minutes = "0" + minutes;
            return hours + ":" + minutes + ":" + seconds;
        } else {
            return minutes + ":" + seconds;
        }
    }

    static format_time_duration(seconds, include_seconds = false) {
        const parts = {
            "year": 31536000,
            "month": 2628000,
            "day": 86400,
            "hour": 3600,
            "minute": 60,
        };

        if (include_seconds) {
            parts["second"] = 1;
        } else if (seconds < 60) {
            return "minute";
        }

        let result = "";

        let remaining_time = seconds;

        for (let [name, duration] of Object.entries(parts)) {
            let part_time = Math.floor(remaining_time / duration);

            if (part_time > 0) {
                remaining_time -= part_time * duration;

                result += pluralise_word(part_time, " " + name) + " ";
            }
        }

        return result.trim();
    }

    static generate_snippet(html, length = 300) {
        let text = ValueHelper.strip_html(html);
        //TODO strigify html

        if (text.length > length) {
            let sentences = text.split(".");

            if (sentences.length > 0) {
                let first_sentence = sentences[0].trim();

                if (first_sentence.length > length) {
                    let words = first_sentence.split(" ");

                    text = "";

                    for (let i = 0; text.length + words[i].length < length; i++) {
                        text += words[i] + " ";
                    }
                    text = text.trim() + "...";
                } else {
                    text = "";
                    for (let i = 0; text.length + sentences[i].length < length; i++) {
                        text += sentences[i] + ". ";
                    }
                }
            } else {
                text = "";
            }
        }


        text = text.replace(/ +/g, " ");

        return text.trim();
    }

    /**
     * Get the number suffix for the specified
     *
     * e.g.
     *  1 -> st
     *  2 -> nd
     *  10 -> th
     *
     * @param {number} value
     * @return {string}
     */
    static get_number_suffix(value) {
        // Try the first 2 digits
        value %= 100;

        const specific_suffixes = {
            10: 'th', // tenth
            11: 'th', // eleventh
            12: 'th', // twelfth
            13: 'th', // thirteenth
        };

        if ('undefined' !== typeof specific_suffixes[value]) {
            return specific_suffixes[value];
        }

        // Then only the first digit
        value %= 10;

        const suffixes = {
            1: 'st',
            2: 'nd',
            3: 'rd',
        };

        if ('undefined' !== typeof suffixes[value]) {
            return suffixes[value];
        }

        return 'th';
    }

    /**
     *
     * @param {string} text
     * @param {number} minimum The minimum read time to be returned
     * @return {number} The estimated time to read the passed text. This value is in seconds
     */
    static get_time_to_read(text, minimum = 5) {
        let words = text.split(" ").map((val) => val.trim()).filter((val) => val !== "");
        let time = 0.5;

        words.forEach((word) => {
            time += 0.21;

            // Allow extra time for longer words
            if (word.length > 3) {
                time += 0.1;
            }

            // If this word contains punctuation (e.g. end of sentence) then allow a little extra time
            if ([".", ",", "!", ";", ":", "?", ")"].indexOf(word[word.length - 1]) !== -1) {
                time += 0.2;
            }
        });

        return Math.max(Math.ceil(time), minimum);
    }

    static htmlentities(str) {
        return eotwJSXFactory('div', null, [str]).html();
    }

    static nl2br(str) {
        return str.replace(/\n/g, "<br>\n");
    }

    /**
     * @deprecated
     * @see URLHelper.normalise_url
     * @param {string} url
     * @returns {string}
     */
    static normalise_url(url) {
        return URLHelper.normalise_url(url);
    }

    static obj_compare(obj1, obj2, noOpposite = null, seenObjects = null) {
        if (typeof obj1 !== typeof obj2) return false;

        if (seenObjects === null) {
            seenObjects = new WeakMap();
        } else if (seenObjects.get(obj1) !== null) {
            return true;
        }
        seenObjects.set(obj1, true);

        let seenKeys = {};
        let same = true;

        $.each(obj1, function (key, val) {
            if ("undefined" !== typeof seenKeys[key]) {
                return;
            }
            seenKeys[key] = true;

            if (typeof val !== typeof obj2[key]) {
                same = false;
            } else if ("object" === typeof val) {
                same = same && ValueHelper.obj_compare(val, obj2[key], null, seenObjects);
            } else if (val !== obj2[key]) {
                same = false;
            }

            if (!same) return false;
        });

        return same && (noOpposite === null || ValueHelper.obj_compare(obj2, obj1, true, seenObjects));
    }

    /**
     * Process the passed name and extract the parts: Title, First Name and Last Name
     *
     * @param {string} name
     *
     * @return {object}
     */
    static process_full_name(name) {
        const data = {
            'title': '',
            'first_name': '',
            'last_name': '',
        };

        /** @var {Array<string>} name_parts */
        const name_parts = name.split(" ");
        if (name_parts.length === 0) {
            return data;
        }

        /** @var {string} first_name */
        let first_name = name_parts.shift();

        if (NAME_TITLES.map((title) => title.toLowerCase()).indexOf(first_name.toLowerCase()) !== -1) {
            data['title'] = first_name;

            if (name_parts.length > 0) {
                first_name = name_parts.shift();
            } else {
                first_name = '';
            }
        }

        data['first_name'] = first_name;
        data['last_name'] = name_parts.join(" ");

        return data;
    }

    static random_string(length = 12, characters_str = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') {
        let characters = characters_str.split("");

        let string = '';
        for (let i = 0; i < length; i++) {
            string += characters[Math.round(Math.random() * (characters.length - 1))];
        }

        return string;
    }

    static str_pad(str, padLength, padStr = "0", padLeft = false) {
        str = "" + str;
        padStr = "" + padStr;
        if (padLength <= str.length) return str;

        let padValue = padStr.repeat(padLength - str.length);

        return padLeft ? padValue + str : str + padValue;
    }

    static strcmp(str1, str2) {
        return ((str1 === str2) ? 0 : ((str1 > str2) ? 1 : -1));
    }

    static stringify_html(html, auto_punctuate = false, allowed_elements = "") {
        //TODO clean input

        // Remove &nbsp; breaks CSVs etc.
        html = html.replace(/&nbsp;/g, " ");

        //Remove all new lines
        html = html.replace(/\n\r/g, "");

        // Add new line after all <p>, <div> and <br> tags
        html = html.replace(/(<\/ *p>)/g, "</p>\n");
        html = html.replace(/(<\/ *div>)/g, "</div>\n");
        html = html.replace(/(<br *\/?>)/g, "<br>\n");

        if (auto_punctuate) {
            //Add missing full stop to the end of <p> and <h2...> tags. Only if they contain any non-punctuation text
            html = html.replace(/([a-zA-Z0-9][^. ]) *(<\/(h[1-9]+|p)>)/g, "$1.$2");
        }

        //Add a space after all tags
        html = html.replace(/(<[^>]+>)/g, "$1");

        //Remove all tags
        //TODO strip tags

        //Replace tabs with spaces
        html = html.replace(/	/g, " ");

        //Remove double spaces
        html = html.replace(/ +/g, " ");

        //Remove leading spaces on lines
        html = html.replace(/^ +/g, "");
        html = html.replace(/\n +/g, "\n");

        return html.trim();
    }

    static strip_html(str) {
        return eotwJSXFactory('div', {unsafeHtml: str}).text();
    }
}

window.ValueHelper = ValueHelper;
export default ValueHelper;

export const NAME_TITLES = [
    "Mr",
    "Mrs",
    "Miss",
    "Ms",
    "Dr",
    "Professor",
];
